import { assert, Command, parse, promptMarker, timeToRenderLine } from '@hackvegas-2025/shared';
import { log, State } from './state';
import { commands } from './commands';
import { playTypingSound } from './audio';

function assertCommandType(command: string): asserts command is Command {
  if (!(command in Command)) throw new Error('Invalid command');
}

export function escapeInteractive(state: State) {
  log(`${promptMarker}^c ${state.prompt}`);
  state.prompt = '';
  state.prompting = false;
}

function backspace(state: State) {
  state.prompt = state.prompt.slice(0, -1);
}

function addChar(state: State, key: string) {
  state.prompt += key;
}

export async function attemptCommand(state: State) {
  // fast forward to end
  const timeToRenderAll = state.lines.reduce((sum, line) => sum + timeToRenderLine(line), 0);
  state.renderLineTime = timeToRenderAll;

  const input = state.prompt.trim();
  state.prompt = '';

  state.lines.push(
    parse(`${promptMarker}${input} `), // echo the prompt
  );
  state.lastLineShown = state.lines.length;

  if (!input) return;

  if (!(input in Command)) {
    log(`Unknown command: ${input}`);
    return;
  }

  assertCommandType(input);

  if (!state.availableCommands.includes(input)) {
    log(`Command not available: ${input}`);
    return;
  }

  const cmd = commands[input];
  await cmd.execute(state);
}

export async function terminalPrompt(state: State, opts?: { cancellable?: boolean }): Promise<[string, boolean]> {
  assert(!state.prompting);
  const { cancellable = true } = opts ?? {};
  const abortController = new AbortController();
  return new Promise((resolve) => {
    state.prompting = true;
    window.addEventListener(
      'keydown',
      (event) => {
        state.scrolled = null;
        if (cancellable) {
          if (event.key === 'Escape' || (event.key === 'c' && event.ctrlKey)) {
            resolve([`^c ${state.prompt}`, true] as const);
            escapeInteractive(state);
            abortController.abort();
            return;
          }
        }

        // don't process input if modifier keys are pressed (hotkeys like cmd+"+")
        if (event.ctrlKey || event.metaKey || event.altKey) return;

        playTypingSound();

        if (event.key === 'Enter') {
          resolve([state.prompt, false] as const);
          abortController.abort();
          state.prompting = false;
        } else if (event.key === 'Backspace') {
          backspace(state);
        } else if (event.key.length === 1) {
          addChar(state, event.key);
        }
      },
      { signal: abortController.signal, passive: true },
    );
  });
}

// for ssh hackertyper game
export function keyCaptureTerminalPrompt(state: State) {
  assert(!state.prompting);
  state.prompting = true;
  const abortController = new AbortController();

  const stream = new ReadableStream<string>({
    start(controller) {
      const onKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' || (event.key === 'c' && event.ctrlKey)) {
          escapeInteractive(state);
          controller.close();
          abortController.abort();
          return;
        }
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        if (event.key.length !== 1) return;
        controller.enqueue(event.key);
      };
      addEventListener('keydown', onKey, { signal: abortController.signal });
    },
    cancel() {
      abortController.abort();
    },
  });

  return {
    async *[Symbol.asyncIterator]() {
      const reader = stream.getReader();
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done || value === undefined) return;
          yield value;
        }
      } finally {
        await reader.cancel().catch(() => {});
        reader.releaseLock();
      }
    },
  };
}
